import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatUnixTimestampToDate } from "utils/formatters";

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const REFRESH_INTERVAL_MS = 30_000;

// Relative time for timestamps ("10 min ago", "3 days ago"); an
// unparseable timestamp falls back to the absolute date.
export const useTimeAgo = (unixTimestamp: string) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const timestamp = Number(unixTimestamp);
  if (!timestamp) return formatUnixTimestampToDate(unixTimestamp);

  const elapsed = Math.max(0, Math.floor(now / 1_000) - timestamp);

  if (elapsed < MINUTE) return t("time_ago.just_now");
  if (elapsed < HOUR)
    return t("time_ago.minutes", { minutes: Math.floor(elapsed / MINUTE) });
  if (elapsed < DAY)
    return t("time_ago.hours", { hours: Math.floor(elapsed / HOUR) });

  const days = Math.floor(elapsed / DAY);
  if (days === 1) return t("time_ago.day");
  return t("time_ago.days", { days });
};
